import React from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  Box as DarkBox,
  IncidentCard,
  IncidentCardDescriptionText,
} from '@hivewatch/dark'

import { formatTimeDistance } from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'
import { toCardPriority, toCardStatus } from '@/utils/incidents'

import type { TeamStaleIncident } from '../types/types'
import { buildCardDescription } from '../utils/utils'

interface StaleIncidentCardIProps {
  /** Pre-derived at the call site from the attributed ack track. */
  acknowledgedAt?: string
  incident: TeamStaleIncident
  onClick: () => void
  /** Renders the bulk-resolve checkbox (ENG-32272, flag + role gated). */
  isSelectable?: boolean
  isSelected?: boolean
  onSelectChange?: (isSelected: boolean) => void
}

/** One clickable incident card in an expanded group section. */
export const StaleIncidentCard = ({
  acknowledgedAt,
  incident,
  onClick,
  isSelectable = false,
  isSelected = false,
  onSelectChange,
}: StaleIncidentCardIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()

  const card = (
    <IncidentCard
      colorScheme={colorScheme}
      cursor='pointer'
      data-testid={`stale-incident-card-${incident.id}`}
      description={
        <IncidentCardDescriptionText colorScheme={colorScheme}>
          {buildCardDescription(incident)}
        </IncidentCardDescriptionText>
      }
      onClick={onClick}
      // IncidentCard renders a plain div; without these the card is
      // unreachable by keyboard (same pattern as the dark package's
      // IncidentCardDescriptionChip clickable variant).
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
      priority={toCardPriority(incident.priority)}
      role='button'
      status={toCardStatus(incident.status)}
      tabIndex={0}
      timestamp={
        acknowledgedAt
          ? tVar(t, 'incidents:stale_card_acknowledged_ago', {
              time: formatTimeDistance(acknowledgedAt),
            })
          : undefined
      }
      title={incident.name ?? incident.displayId}
    />
  )

  if (!isSelectable) return card

  // The checkbox sits BESIDE the card, not inside it: the whole card is a
  // click target that opens the incident drawer, and nesting a control
  // inside a role='button' both misreads to screen readers and needs
  // propagation hacks.
  return (
    <DarkBox alignItems='center' display='flex' gap='8px'>
      <Checkbox
        aria-label={tVar(t, 'incidents:stale_bulk_select_incident', {
          name: incident.name ?? incident.displayId,
        })}
        checked={isSelected}
        colorScheme={colorScheme}
        data-testid={`stale-incident-select-${incident.id}`}
        onChange={(event) => onSelectChange?.(event.target.checked)}
      />
      <DarkBox flex='1' minW='0'>
        {card}
      </DarkBox>
    </DarkBox>
  )
}
